/**
 * Every string the account detail renders, transcribed from the UI/UX pack.
 *
 * Two wording rules carry weight on this page. The context line under a heading
 * states **the one consequence the operator cannot see from the screen** — that
 * the number is a credential its owner alone changes, that a grant does not
 * renew, that a role is granted and never asked for. And no line explains why a
 * field is read-only in terms of a catalog row: the screen states the
 * consequence, this repo's PRD-TDD states the reason.
 */
export const copy = {
  head: {
    lede: 'حساب واحد بكلّ ما يخصّه. أربعة أفعال تقع من هذه الصفحة: تغيير الدور، وتحرير النبذة، ومنح استحقاق، وحظر الحساب. ما عداها يُعرض للقراءة.',
  },

  /** §1 الملفّ — read, never written, and the line says where each field is edited. */
  profile: {
    heading: 'الملفّ',
    handle: 'المعرّف',
    phone: 'رقم الهاتف',
    role: 'الدور',
    state: 'حالة الحساب',
    note: 'رقم الهاتف هو اعتماد الدخول: يغيّره صاحب الحساب وحده من إعداداته، وبرمز يصل إلى الرقم الجديد. والاسم والمعرّف والصورة يحرّرها هو أيضاً — تُعرض هنا للقراءة.',
  },

  /** §2 الأدوار — two registries, one dialog, and each control grants and revokes. */
  roles: {
    heading: 'الدور',
    edit: 'غيّر الدور',
    context:
      'لكلّ حساب دورٌ واحد، يُكتب في كلّ خدمة تعرفه. «غيّر الدور» يمنح ويسحب معاً — والسحب هو إعادة الحساب إلى «مشترك». الأدوار يمنحها المدير ولا يطلبها أصحابها.',
    accountRole: 'دور الحساب',
    accountOpens: 'ما يفتحه',
    accountOpensValue: 'التأليف والإثراء والتصنيفات والرفوف والخطط وطوابير الإشراف والحالات.',
    twoPerson:
      'المقال يوافق عليه غيرُ كاتبه — كلّ مشغّل يراجع، ولا أحد يوافق على ما كتبه هو، فلا يُغني مشغّلٌ واحد عن ثانٍ.',
    /** §2.4's conditional grant, stated rather than implied by the badge. */
    notYetEffective: 'الدور الجديد لا يظهر في القوائم العامّة قبل أن يقع الفعل المؤهِّل له.',

    dialog: {
      title: (name: string) => `دور «${name}»`,
      requiredNote: 'الحقول المعلَّمة بـ* مطلوبة.',
      accountLabel: 'دور الحساب',
      accountHint:
        '«مشغّل» يفتح التأليف والإثراء والتصنيفات والرفوف والخطط وطوابير الإشراف ومراجعة المقالات. و«مدير» يضيف إليها الحظر ومنح الأدوار ومنح الاستحقاقات والتهيئة.',
      save: 'احفظ الدور',
    },

    confirm: {
      heading: (name: string) => `تغيير دور «${name}»؟`,
      /** The escalation is named before it applies — the confirm step is the only guard that ships. */
      body: (from: string, to: string) =>
        `ينتقل الحساب من «${from}» إلى «${to}». تسري الصلاحية فور الحفظ، ولا يبقى أثر يُراجَع بعده.`,
      commit: 'احفظ الدور',
    },

    saved: 'حُفظ الدور.',
    /**
     * The partial write, named service by service.
     *
     * Seven services take the grant on their own route and nothing spans them,
     * so a run that lands five and fails two has no rollback to offer — what it
     * has is the truth about which two, and «أعد المحاولة» meaning exactly the
     * same submit again, which is idempotent on every one of them.
     */
    partial: (services: string) => `حُفظ الدور، ولم يصل إلى: ${services}. أعد الحفظ نفسه — التكرار لا يضرّ.`,
  },

  /** §3 النبذة — present only because the account holds a role. */
  biography: {
    heading: 'النبذة',
    edit: 'حرّر النبذة',
    context:
      'النصّ الذي يظهر على الملفّ العامّ لهذا الحساب. يحرّره صاحبه من إعداداته، وتستطيع تحريره من هنا.',
    empty: 'لا نبذة على هذا الحساب بعد.',

    dialog: {
      title: (name: string) => `نبذة «${name}»`,
      requiredNote: 'الحقول المعلَّمة بـ* مطلوبة.',
      label: 'النصّ',
      hint: 'يظهر على الملفّ العامّ لهذا الحساب. يستطيع صاحبه تحريره من إعداداته أيضاً.',
      save: 'احفظ النبذة',
    },

    saved: 'حُفظت النبذة.',
  },

  /** §4 الاستحقاقات — two sources, and the two absences are stated. */
  entitlements: {
    heading: 'الاستحقاقات',
    grant: 'امنح استحقاقاً',
    context:
      'كلّ ما يفتح الفهرس لهذا الحساب. الاشتراك يتجدّد تلقائياً عند حلول تاريخه؛ والمنحة اليدوية تنتهي بتاريخها ولا تتجدّد.',
    caption: 'استحقاقات الحساب بمصدرها ونطاقها ومدّتها',
    columns: {
      source: 'المصدر',
      scope: 'النطاق',
      plan: 'الخطّة',
      starts: 'التفعيل',
      ends: 'الانتهاء',
      state: 'الحالة',
    },
    /** `ENTITLEMENT_MODES` members, worded. A value with no entry renders verbatim. */
    sources: {
      SUBSCRIPTION: 'اشتراك',
      MANUAL_GRANT: 'منحة يدوية',
    } as Readonly<Record<string, string>>,
    /** `DEFAULT_ENTITLEMENT_SCOPE` is the catalogue; the others are legal future values. */
    scopes: {
      CATALOGUE: 'الفهرس كاملاً',
      TITLE: 'عنوان واحد',
      COLLECTION: 'مجموعة',
    } as Readonly<Record<string, string>>,
    states: {
      SCHEDULED: 'لم يبدأ بعد',
      ACTIVE: 'فاعل',
      EXPIRED: 'منتهٍ',
    } as Readonly<Record<string, string>>,
    /** An entitlement whose liveness the wire did not state. Never computed here. */
    stateUnknown: 'غير مذكورة',
    noPlan: 'لا خطّة على المنحة',
    noEnd: 'بلا انتهاء',
    outro: 'الوصول يُمنح للفهرس بأكمله لا لعنوان بعينه. المبالغ والمدفوعات في تقارير الاشتراك.',
    empty: 'لا استحقاق على هذا الحساب. لا يفتح الفهرس له شيء الآن.',

    dialog: {
      title: (name: string) => `امنح استحقاقاً لـ«${name}»`,
      requiredNote: 'الحقول المعلَّمة بـ* مطلوبة.',
      scopeLead: 'المنحة تفتح',
      scopeStrong: 'الفهرس كاملاً',
      scopeRest: '— ما يفتحه الاشتراك بالضبط.',
      expiresLabel: 'تاريخ الانتهاء',
      expiresHint: 'ينتهي الاستحقاق في هذا التاريخ ولا يتجدّد تلقائياً.',
      /** The read-back: what this press will do, and when it stops doing it. */
      readBackLead: 'يفتح هذا الحسابُ الفهرسَ كاملاً حتّى',
      grant: 'امنح الاستحقاق',
    },

    granted: 'مُنح الاستحقاق.',
  },

  /** §5 سجلّ الحالات — the queue's own records, opened from here. */
  cases: {
    heading: 'سجلّ الحالات',
    queueLink: 'طابور الحالات ←',
    context: 'الحالات التي فتحها هذا الحساب أو فُتحت عليه. افتح أيّ صفّ للردّ عليه أو تغيير وضعه.',
    caption: 'حالات هذا الحساب بنوعها ووضعها وآخر تحديث لها',
    columns: {
      type: 'النوع',
      subject: 'الموضوع',
      reference: 'المعرّف',
      state: 'الوضع',
      updated: 'آخر تحديث',
    },
    /** The five code-owned case types, worded. A sixth would render as its own token. */
    types: {
      SUPPORT_TICKET: 'تذكرة دعم',
      CONTENT_REPORT: 'بلاغ محتوى',
      USER_REPORT: 'بلاغ حساب',
      CONTENT_REQUEST: 'طلب عنوان',
      DEFECT_REPORT: 'بلاغ عطل',
    } as Readonly<Record<string, string>>,
    /**
     * `CASE_STATES` wording. **The set is the service's, not this map's** — the
     * rows carry their own state token and a value with no entry here renders
     * verbatim beside a neutral badge. Each surface owns its own wording, which
     * is why «بانتظار المستخدم» here and «بانتظار المُبلِّغ» on the report
     * surfaces are one value read twice.
     */
    states: {
      open: 'مفتوحة',
      'awaiting user': 'بانتظار المستخدم',
      resolved: 'تمّت المعالجة',
      closed: 'مغلقة',
    } as Readonly<Record<string, string>>,
    noSubject: 'بلا موضوع',
    empty: 'لا حالة على هذا الحساب.',
  },

  states: {
    loading: 'جارٍ التحميل',
    notFound: 'لم نعثر على هذا الحساب.',
  },
} as const;
